import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Shared pagination control used across list pages.
 * - Prev / page-numbers / Next, with a compact window (1 … 4 5 6 … 12).
 * - Responsive: Prev/Next show labels on sm+, chevrons-only on mobile.
 * - Renders nothing when there's only one page.
 *
 * Props:
 *   page (number)        current page (1-based)
 *   totalPages (number)  total number of pages
 *   onChange (fn)        called with the new page number
 */
const Pagination = ({ page, totalPages, onChange, className = '' }) => {
  if (!totalPages || totalPages <= 1) return null;

  const go = (p) => {
    if (p >= 1 && p <= totalPages && p !== page) onChange(p);
  };

  // Build a compact list of page numbers with ellipses.
  const items = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
      items.push(p);
    } else if (items[items.length - 1] !== '…') {
      items.push('…');
    }
  }

  const navBtn = 'inline-flex items-center h-9 px-3 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className={`flex items-center justify-center gap-1 mt-4 ${className}`}>
      <button type="button" onClick={() => go(page - 1)} disabled={page <= 1} className={navBtn} aria-label="Previous page">
        <ChevronLeft className="h-4 w-4" />
        <span className="hidden sm:inline ml-1">Prev</span>
      </button>

      {items.map((p, i) =>
        p === '…' ? (
          <span key={`e${i}`} className="px-2 text-gray-400 select-none">…</span>
        ) : (
          <button
            type="button"
            key={p}
            onClick={() => go(p)}
            aria-current={p === page ? 'page' : undefined}
            className={`h-9 min-w-[36px] px-2 rounded-lg text-sm font-medium border ${
              p === page
                ? 'bg-blue-600 text-white border-blue-600'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {p}
          </button>
        )
      )}

      <button type="button" onClick={() => go(page + 1)} disabled={page >= totalPages} className={navBtn} aria-label="Next page">
        <span className="hidden sm:inline mr-1">Next</span>
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

export default Pagination;
